import { ContentAnalyticsContentType } from 'shared/api/requests/analytics/schemas'

export interface QueueItem {
  id: string
  type: ContentAnalyticsContentType
}

const REQUEST_GAP_MS = 200

const queue: QueueItem[] = []
const enqueuedIds = new Set<string>()

interface DrainOwner {
  releaseDelay?: () => void
}

let activeDrain: DrainOwner | undefined

type DispatchItem = (item: QueueItem) => Promise<unknown>

export const enqueueItemFetch = (item: QueueItem, dispatchItem: DispatchItem) => {
  if (enqueuedIds.has(item.id)) {
    return undefined
  }

  enqueuedIds.add(item.id)
  queue.push(item)

  if (!activeDrain) {
    const owner: DrainOwner = {}
    activeDrain = owner
    // Callers may await only the drain started by this enqueue.
    return drain(dispatchItem, owner)
  }

  return undefined
}

const waitForNextRequest = (owner: DrainOwner) =>
  new Promise<void>((resolve) => {
    const release = () => {
      clearTimeout(timer)
      owner.releaseDelay = undefined
      resolve()
    }
    const timer = setTimeout(release, REQUEST_GAP_MS)
    owner.releaseDelay = release
  })

const drain = async (dispatchItem: DispatchItem, owner: DrainOwner) => {
  try {
    while (activeDrain === owner && queue.length > 0) {
      const item = queue.shift() as QueueItem

      try {
        await dispatchItem(item)
      } catch {}

      if (activeDrain !== owner) {
        return
      }

      if (queue.length > 0) {
        await waitForNextRequest(owner)
      }
    }
  } finally {
    if (activeDrain === owner) {
      activeDrain = undefined
    }
  }
}

export const resetItemFetchQueue = () => {
  const previousDrain = activeDrain
  activeDrain = undefined
  queue.length = 0
  enqueuedIds.clear()
  // Release the wait as well as its timer; an in-flight dispatch settles normally.
  previousDrain?.releaseDelay?.()
}

export const ContentFetchQueue = {
  enqueueItemFetch,
  resetItemFetchQueue,
}
